import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import type {
  AgenticDocumentSearchResult,
  AgenticSearchErrorEvent,
  AgenticSearchThoughtEvent,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { SelectedTab } from 'features/navbar/types'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'
import { z } from 'zod'

import type { NormalizedBBox } from './types/ocrViewer'
export type ViewMode = 'card' | 'table'
export type DocumentSearchMode = 'normal' | 'agentic'

export enum DocumentViewerPage {
  LEFT_PAGE = 0,
  RIGHT_PAGE = 1,
}

export interface AgenticSubmittedQueryEvent {
  id: string
  type: 'submitted_query'
  query: string
}

export interface AgenticThinkingEvent {
  id: string
  type: 'thinking'
  query: string
}

export type AgenticConversationThoughtEvent = AgenticSearchThoughtEvent & {
  id: string
}

export type AgenticConversationResponseEvent = Omit<
  AgenticDocumentSearchResult,
  'results'
> & {
  id: string
  resultCount: number
}

export type AgenticConversationErrorEvent = AgenticSearchErrorEvent & {
  id: string
}

export type AgenticConversationEvent =
  | AgenticSubmittedQueryEvent
  | AgenticThinkingEvent
  | AgenticConversationThoughtEvent
  | AgenticConversationResponseEvent
  | AgenticConversationErrorEvent

export type AgenticConversationTurnStatus = 'loading' | 'complete' | 'error'

export interface AgenticConversationTurn {
  id: string
  query: string
  events: AgenticConversationEvent[]
}

export interface AgenticSearchConversation {
  id?: string
  activeTurnId?: string
  turns: AgenticConversationTurn[]
}

export interface DestinationHistoryItem {
  type: 'destination'
  /** Usually a SelectedTab value; footer/settings links may use a label key. */
  destinationKey: string
  label: string
  href: string
}

export interface SearchHistoryItem {
  type: 'search'
  searchState: {
    contentFilter: DocumentsViewerContentFilter
    searchMode: DocumentSearchMode
    viewMode: ViewMode
    query: string
    inputQuery?: string
    browseAll?: boolean
    filters: ColumnFiltersState
    sorting: SortingState
    pagination: PaginationState
    selectedDocumentId: string | null
    agenticSearch?: {
      siteId: string
      conversation: AgenticSearchConversation
    }
  }
  linkedDocuments?: {
    referenceText: string
    documentIds: string[]
    unitId?: string
  }
}

export type SearchStateUpdate =
  | Partial<SearchHistoryItem['searchState']>
  | ((
      currentSearchState: SearchHistoryItem['searchState']
    ) => Partial<SearchHistoryItem['searchState']>)

export interface FragmentHighlight {
  pageNum: number
  fragmentText: string
}

/**
 * A cited document region to highlight and auto-frame, as normalized
 * page-fraction rects (the evidence panels' bbox citations). Unlike
 * `selectedBoxIds` this does not depend on OCR block ids, so it works for
 * coordinate-only citations.
 */
export interface RegionHighlight {
  pageNum: number
  boxes: NormalizedBBox[]
}

/**
 * Where a reference jump came from: set on the history item pushed by clicking
 * a doc-reference (off-page connector / drawing number) box, so the viewer can
 * offer an explicit "Back to <origin>" affordance on top of the generic
 * history back. The origin state itself lives in the previous history item.
 */
export interface ReferenceJumpOrigin {
  documentId: string
  /** Human label for the origin document (its drawing number or title). */
  label: string
}

export interface DocumentHistoryItem {
  type: 'document'
  documentId: string
  pageNumber: number
  selectedBoxIds: string[]
  selectedSubjectUid?: string
  query: string
  searchOriginQuery?: string
  fragmentHighlight?: FragmentHighlight
  regionHighlight?: RegionHighlight
  referenceOrigin?: ReferenceJumpOrigin
}

export interface TimeSeriesHistoryItem {
  type: 'timeseries'
  sensorIds: string[]
  label: string
  /**
   * Saved chart backing this tab. When set, the tab renders the SAVED chart
   * with full fidelity (pens, colors, y-axis config) via the chart resolve
   * flow, and `sensorIds` seeding is ignored. Sensor-seeded tabs leave this
   * unset. Set at open time (Recent Chart card) or when a Save inside the tab
   * promotes the transient chart into a real entry.
   */
  chartID?: string
  timeWindow?: { from: string; to: string }
  autoOpenTagSearch?: boolean
  /** Per-sensor line colors (e.g. claims: primary green, related a palette). */
  sensorColors?: Record<string, string>
  /** Sensor ids to render de-emphasized (dimmed/dotted). */
  deemphasizedSensorIds?: string[]
  /**
   * When true, `sensorIds` is a CONTROLLED set: the chart's selection is
   * replaced to exactly match on every change (add + remove), rather than the
   * default additive seeding. Used by the claims pinned process-data tab so the
   * sidebar can both add and remove pens live without remounting the chart.
   */
  controlled?: boolean
}

export type TabHistoryItem =
  | SearchHistoryItem
  | DocumentHistoryItem
  | TimeSeriesHistoryItem
  | DestinationHistoryItem

export interface DocumentTab {
  id: string
  history: TabHistoryItem[]
  currentIndex: number
  customLabel?: string
  workspaceDestination?: SelectedTab
  /**
   * When false, the tab is pinned: it cannot be closed or dragged, and consumers
   * keep it left-most. Defaults to closable when omitted.
   */
  closable?: boolean
}

export const viewLayoutSchema = z.enum(['continuous', 'twoPage', 'single'])
export type ViewLayout = z.infer<typeof viewLayoutSchema>

export enum TooltipOption {
  ShowTooltips = 'showTooltips',
  ShowMultiplePages = 'showMultiplePages',
  MultiPageTurner = 'multiPageTurner',
  AutoOpenNewTab = 'autoOpenNewTab',
}

export enum FilterOption {
  ShowUnconfirmed = 'showUnconfirmed',
  ShowConfirmed = 'showConfirmed',
  ShowAll = 'showAll',
  RemoveBoxes = 'removeBoxes',
}
